import { useCallback, useRef, useEffect } from 'react';
import { useUser } from './UserContext';

// Benzersiz oturum ID üret
function getSessionId() {
  let sid = sessionStorage.getItem('activity_session_id');
  if (!sid) {
    sid = 'ses_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem('activity_session_id', sid);
  }
  return sid;
}

// Log kuyruğu - toplu gönderim için
let logQueue = [];
let flushTimer = null;

function flushQueue() {
  if (logQueue.length === 0) return;
  const batch = [...logQueue];
  logQueue = [];
  fetch('/api/activity-log/batch', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ logs: batch })
  }).catch(() => {
    // Hata durumunda kuyruğa geri ekle
    logQueue = [...batch, ...logQueue];
  });
}

function queueLog(logEntry) {
  logQueue.push(logEntry);
  if (flushTimer) clearTimeout(flushTimer);
  // 3 saniye sonra veya 10 log birikince gönder
  if (logQueue.length >= 10) {
    flushQueue();
  } else {
    flushTimer = setTimeout(flushQueue, 3000);
  }
}

// Sayfa kapanırken kalan logları gönder
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (logQueue.length > 0) {
      const batch = [...logQueue];
      logQueue = [];
      // sendBeacon ile garanti gönderim
      navigator.sendBeacon('/api/activity-log/batch', JSON.stringify({ logs: batch }));
    }
  });
}

export function useActivityTracker() {
  const { user, selectedFirma } = useUser();
  const modalTimers = useRef({});

  const getUserInfo = useCallback(() => ({
    user_id: user?.id || null,
    user_email: user?.email || null,
    user_name: user?.name || user?.firma || null,
    user_role: user?.role || user?.rol || null,
    firma: selectedFirma?.firma_adi || user?.firma || null,
    session_id: getSessionId(),
    page_url: window.location.pathname
  }), [user, selectedFirma]);

  // Genel log fonksiyonu
  const trackAction = useCallback((actionType, detail = {}, immediate = false) => {
    const entry = {
      ...getUserInfo(),
      action_type: actionType,
      action_detail: detail
    };
    if (immediate) {
      fetch('/api/activity-log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(entry)
      }).catch(() => {});
    } else {
      queueLog(entry);
    }
  }, [getUserInfo]);

  // === Önceden tanımlı tracking fonksiyonları ===

  // Giriş yapma
  const trackLogin = useCallback((extraDetail = {}) => {
    trackAction('login', { ...extraDetail, timestamp: new Date().toISOString() }, true);
  }, [trackAction]);

  // Çıkış yapma
  const trackLogout = useCallback(() => {
    trackAction('logout', { timestamp: new Date().toISOString() }, true);
  }, [trackAction]);

  // Sayfa görüntüleme
  const trackPageView = useCallback((pageName, extraDetail = {}) => {
    trackAction('page_view', { page: pageName, ...extraDetail });
  }, [trackAction]);

  // Ürün listesinde ürün görme (scroll ile)
  const trackProductView = useCallback((product) => {
    trackAction('product_view', {
      product_id: product.id,
      product_name: product.ad || product.name,
      product_code: product.code || product.stok_kodu,
      category: product.kategori || product.parent_group
    });
  }, [trackAction]);

  // Ürün detayına tıklama (modal açma)
  const trackProductDetailOpen = useCallback((product) => {
    const key = `product_${product.id || product.name}`;
    modalTimers.current[key] = Date.now();
    trackAction('product_detail_open', {
      product_id: product.id,
      product_name: product.ad || product.name,
      product_code: product.code || product.stok_kodu,
      category: product.kategori || product.parent_group
    });
  }, [trackAction]);

  // Ürün detayından çıkma (modal kapama)
  const trackProductDetailClose = useCallback((product) => {
    const key = `product_${product.id || product.name}`;
    const openTime = modalTimers.current[key];
    const durationMs = openTime ? Date.now() - openTime : 0;
    const durationSec = Math.round(durationMs / 1000);
    delete modalTimers.current[key];
    trackAction('product_detail_close', {
      product_id: product.id,
      product_name: product.ad || product.name,
      product_code: product.code || product.stok_kodu,
      duration_seconds: durationSec
    });
  }, [trackAction]);

  // Sepete ürün ekleme
  const trackCartAdd = useCallback((product, quantity = 1) => {
    trackAction('cart_add', {
      product_id: product.id,
      product_name: product.ad || product.name,
      product_code: product.code || product.stok_kodu,
      variant: product.variant || product.selectedVariant,
      quantity
    });
  }, [trackAction]);

  // Sepetten ürün çıkarma
  const trackCartRemove = useCallback((product) => {
    trackAction('cart_remove', {
      product_id: product.id,
      product_name: product.ad || product.name,
      product_code: product.code || product.stok_kodu,
      variant: product.variant || product.selectedVariant
    });
  }, [trackAction]);

  // Sipariş oluşturma
  const trackOrderCreate = useCallback((orderDetail = {}) => {
    trackAction('order_create', orderDetail, true);
  }, [trackAction]);

  // Arama yapma
  const trackSearch = useCallback((searchTerm, resultCount) => {
    trackAction('search', { term: searchTerm, result_count: resultCount });
  }, [trackAction]);

  // Kategori/filtre değiştirme
  const trackFilter = useCallback((filterType, filterValue) => {
    trackAction('filter_change', { filter_type: filterType, filter_value: filterValue });
  }, [trackAction]);

  // Genel modal açma/kapama
  const trackModalOpen = useCallback((modalName, detail = {}) => {
    modalTimers.current[modalName] = Date.now();
    trackAction('modal_open', { modal: modalName, ...detail });
  }, [trackAction]);

  const trackModalClose = useCallback((modalName, detail = {}) => {
    const openTime = modalTimers.current[modalName];
    const durationSec = openTime ? Math.round((Date.now() - openTime) / 1000) : 0;
    delete modalTimers.current[modalName];
    trackAction('modal_close', { modal: modalName, duration_seconds: durationSec, ...detail });
  }, [trackAction]);

  // Sayfa kapatma durumunda kalan logları gönder
  useEffect(() => {
    return () => {
      flushQueue();
    };
  }, []);

  return {
    trackAction,
    trackLogin,
    trackLogout,
    trackPageView,
    trackProductView,
    trackProductDetailOpen,
    trackProductDetailClose,
    trackCartAdd,
    trackCartRemove,
    trackOrderCreate,
    trackSearch,
    trackFilter,
    trackModalOpen,
    trackModalClose
  };
}
